import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { useGoogleLogin } from '@react-oauth/google';
import { useContextHook } from 'use-context-hook';
import OnboardingTemplate from '../OnboardingTemplate';
import { HeaderLink } from '../Onboarding/FlowTemplate/FlowTemplate.styles';
import { AuthFooterLink, AuthFormShell } from '../Onboarding/AuthForm.styles';
import Form, { useForm } from '../../molecules/Form';
import Field from '../Field';
import Button from '../../molecules/Button';
import authService from '../../../services/authService';
import Toast from '../../molecules/Toast';
import { AuthContext } from '../../../Context/authContext';
import { getReferralCode, setReferralCode, clearReferralCode } from '../../../helpers/referralStorage';
import { getCookie, setCookie } from '../../../helpers/common';

function Signup() {
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getReferralCookieDomain = () => {
    const hostname = window?.location?.hostname || '';
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return undefined;
    if (hostname.endsWith('.houdiny.ai') || hostname === 'houdiny.ai') return '.houdiny.ai';
    return undefined;
  };

  useEffect(() => {
    const referral = searchParams.get('via');
    const promotekitReferral = window?.promotekit_referral;
    const referralFromCookie = getCookie('referral_code');

    const referralCode = promotekitReferral || referral || referralFromCookie;
    if (!referralCode) return;

    setReferralCode(referralCode);
    setCookie('referral_code', referralCode, 30, getReferralCookieDomain());
  }, [searchParams]);

  const { onGoogleLogin, loading_user_google } = useContextHook(AuthContext, v => ({
    onGoogleLogin: v.onGoogleLogin,
    loading_user_google: v.loading_user_google,
  }));

  const googleLogin = useGoogleLogin({
    onSuccess: async tokenResponse => {
      const { access_token } = tokenResponse;
      onGoogleLogin({ access_token, action: 'Register' });
    },
  });

  const handleSubmit = async e => {
    setLoading(true);
    try {
      const payload = {
        email: e?.email,
        password: e?.password,
        fullName: e?.name,
        referral_code: window?.promotekit_referral || getReferralCode() || getCookie('referral_code'),
      };
      const resp = await authService.register(payload);
      localStorage.setItem('email', e?.email);
      if (resp.success) {
        const stripeCustomerId = resp?.data?.stripeCustomerId;
        if (window?.promotekit?.refer) {
          try {
            if (stripeCustomerId) {
              window.promotekit.refer(e?.email, stripeCustomerId);
            } else {
              window.promotekit.refer(e?.email);
            }
          } catch (trackErr) {
            console.log('PromoteKit refer tracking failed:', trackErr?.message || trackErr);
          }
        }
        clearReferralCode();
        setCookie('referral_code', '', -1, getReferralCookieDomain());
        await authService.sendOtp({ email: e?.email });
        navigate('/verify-otp');
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingTemplate
      headerExtra={
        <HeaderLink>
          Already have an account? <Link to="/">Sign in</Link>
        </HeaderLink>
      }>
      <AuthFormShell>
        <div className="auth-heading">
          <h1>Build your <span className="gradient-text">outbound machine.</span></h1>
          <p>Find leads, personalize outreach, and launch your first campaign in minutes.</p>
        </div>

        <Form form={form} onSubmit={handleSubmit}>
          <Form.Item
            label="Full name"
            type="text"
            name="name"
            placeholder="Enter your full name"
            rules={[
              { required: true, message: 'Name is required' },
              { pattern: /^[A-Za-z\s]+$/, message: 'Only letters and spaces are allowed' },
            ]}>
            <Field />
          </Form.Item>

          <Form.Item
            label="Work email"
            type="email"
            name="email"
            placeholder="Enter your work email"
            rules={[{ required: true, message: 'Email is required' }]}>
            <Field />
          </Form.Item>

          <Form.Item
            label="Password"
            type="password"
            name="password"
            placeholder="At least 8 characters"
            rules={[{ required: true, message: 'Password is required' }]}>
            <Field />
          </Form.Item>

          <div className="text-box">
            <Form.Item
              noMargin
              type="checkbox"
              name="terms"
              label={
                <div className="privacy">
                  <p>
                    By continuing you agree to our <a href="https://houdiny.ai/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms</a> &{' '}
                    <a href="https://houdiny.ai/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy</a>.
                  </p>
                </div>
              }
              rules={[{ required: true, message: 'Please accept the terms' }]}>
              <Field />
            </Form.Item>
          </div>

          <div className="btn-holder">
            <Button rounded loader={loading} disabled={loading} variant="onboarding-gradient">
              Start building
            </Button>
          </div>

          <div className="seprator">
            <span className="text">or</span>
          </div>

          <button type="button" className="googleAuth" onClick={googleLogin} disabled={loading_user_google}>
            <div className="img-holder">
              <FcGoogle />
            </div>
            Continue with Google
          </button>
        </Form>

        <AuthFooterLink>
          Already have an account? <Link to="/">Sign in</Link>
        </AuthFooterLink>
      </AuthFormShell>
    </OnboardingTemplate>
  );
}

export default Signup;
